import React, { lazy, useEffect, useLayoutEffect } from "react";

import * as Sentry from "@sentry/react";
import { routes } from "Constant/routes";
import LandingPages from "pages/Trackers/LandingPages";
import AddEditLandingPage from "pages/Trackers/LandingPages/AddEditLandingPage";
import LandingPageReportsPage from "pages/Trackers/LandingPages/LandingPageReportsPage";
import { useDispatch, useSelector } from "react-redux";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { ClipLoader } from "react-spinners";

import { getPiiData } from "helpers/business/getBusiness";

// import * as Sentry from "@sentry/react";
import ErrorBoundary from "components/Layouts/ErrorBoundary";
import Sidebar from "components/Layouts/SidebarManager";
import ModalRoot from "components/organisms/ModalRoot";
import SignalWizard from "components/organisms/SignalWizard";

import "./App.css";
import LeadsWizardComponent from "./components/Leads/LeadsWizardComponent";
import SwalDevPanel from "./components/organisms/DevTools/SwalDevPanel";
import AppLoader from "./components/shared/AppLoader";
import Footer from "./components/shared/Footer";
import Header from "./components/shared/Header";
import { getStartedData } from "./helpers/getStarted/getStarted";
import { PREFETCH_ICON_PATHS } from "./helpers/images/iconManifest";
import useImagePreload from "./hooks/useImagePreload";
import useScrollToTop from "./hooks/useScrollToTop";
import ActivityLog from "./pages/ActivityLog/ActivityLog";
import Destinations from "./pages/Destinations/Destinations.js";
import EditDestination from "./pages/Destinations/EditDestination/EditDestination.js";
import Home from "./pages/Home/Home";
import LeadConnectionReportsPage from "./pages/LeadConnectionReportsPage";
import LeadHistoryPage from "./pages/LeadConnectionReportsPage/LeadHistoryPage";
import LeadDebugPage from "./pages/LeadDebugPage";
import LeadGeneralReportsPage from "./pages/LeadGeneralReportsPage";
import Connection from "./pages/LeadsPages/Connection";
import ConnectionDetail from "./pages/LeadsPages/ConnectionDetail";
import LeadDestinations from "./pages/LeadsPages/LeadDestinations";
import LeadSources from "./pages/LeadsPages/LeadSources";
import Services from "./pages/LeadsPages/Services";
import ErrorPage from "./pages/Moduls/errors";
import NotificationsPage from "./pages/Notifications/NotificationsPage";
import EditSource from "./pages/Sources/EditSources/EditSource.js";
import Sources from "./pages/Sources/Sources.js";
import TrackerReports from "./pages/TrackerReports";
import SingleTracker from "./pages/Trackers/SingleTracker";
import TrackerEventMappingForm from "./pages/Trackers/TrackerEvents/TrackerEventMappingForm";
import TrackerEventsList from "./pages/Trackers/TrackerEvents/TrackerEventsList";
import Trackers from "./pages/Trackers/Trackers";
import TrackerSetting from "./pages/Trackers/TrackerSetting/TrackerSetting";
import { setIsAppLoading } from "./redux/slices/app/app.slice.js";
import { getBusinessAsync } from "./redux/slices/dashboardDatas/dashboardDatas.asyncActions.js";
import { loginFalse, loginTrue } from "./redux/slices/isLogin/isLogin.slice.js";
import { setAppIdRedux } from "./redux/slices/setAppId/setAppId.slice.js";

const HTMLEditor = lazy(() => import("pages/HTMLEditor"));

const isProduction = process.env.NODE_ENV === "production";

const Container = ({ children }) => {
    return isProduction ? (
        <Sentry.ErrorBoundary fallback={<ErrorPage />}>
            {children}
        </Sentry.ErrorBoundary>
    ) : (
        <ErrorBoundary>{children}</ErrorBoundary>
    );
};

const Main = () => {
    useScrollToTop();
    // Warms every integration icon once per session, so the create modals and
    // connection lists behind these routes render without a fetch.
    useImagePreload(PREFETCH_ICON_PATHS);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isAppLoading = useSelector((state) => state.app.isAppLoading);
    const appIdStatus = useSelector((state) => state.setAppId.status);
    const location = useLocation();
    const bussiness = useSelector(
        (state) => state.allDashboardDatasState.getAllDashboardDatas.bussiness,
    );
    const isFullScreenRoute =
        location.pathname.includes("create-landing-page") ||
        location.pathname.includes("landing-pages/edit") ||
        location.pathname.includes("landing-pages/clone") ||
        location.pathname === routes.wizard;
    const tokenTrue = useSelector((state) => state.isLogin.value);
    const userCheckLoading = useSelector(
        (state) => state.isLogin.userCheckLoading,
    );

    useEffect(() => {
        if (!appIdStatus) {
            getStarted();
        }
    }, [appIdStatus]);
    const getStarted = async () => {
        let result = await getStartedData();
        if (result.status) {
            dispatch(setAppIdRedux(result?.data?.data ?? ""));
        } else {
        }
    };
    const handleUserCheck = async () => {
        const response = await getPiiData();
        if (response.status) dispatch(loginTrue());
        else dispatch(loginFalse());
    };
    useEffect(() => {
        dispatch(getBusinessAsync());
        handleUserCheck();
    }, []);

    useEffect(() => {
        if (bussiness && bussiness.length > 0) {
            dispatch(setIsAppLoading(false));
        }
    }, [bussiness]);

    useLayoutEffect(() => {
        let token = window.localStorage.getItem("token");
        const search = window.localStorage.getItem("search");
        if (token && !userCheckLoading && tokenTrue) {
            const to = window.localStorage.getItem("to");
            if (to) {
                navigate(`${to}${search || ""}`);
                localStorage.removeItem("to");
                localStorage.removeItem("search");
            }
        }
    }, [tokenTrue, userCheckLoading]);
    return (
        <div className="app-admin-wrap layout-sidebar-vertical sidebar-full">
            <Container>
                {isAppLoading && <AppLoader />}
                <Sidebar />
                <div
                    className={
                        isFullScreenRoute
                            ? ""
                            : [
                                  "main-content-wrap",
                                  "mobile-menu-content",
                                  "m-0",
                                  "theme-v2",
                                  location.pathname === "/leads-wizard" &&
                                      location.hash === "#step5" &&
                                      "width-full",
                                  location.pathname === "/leads-wizard" &&
                                      "leads-wizard-shell",
                              ]
                                  .filter(Boolean)
                                  .join(" ")
                    }
                >
                    <Header />
                    <ModalRoot />
                    <React.Suspense
                        fallback={
                            <div
                                className="app-loader"
                                style={{ width: "80%" }}
                            >
                                <ClipLoader color="#729abf" size={50} />
                            </div>
                        }
                    >
                        <Routes>
                            <Route element={<Home />} index />
                            <Route element={<Sources />} path="sources" />
                            <Route element={<Trackers />} path="trackers" />
                            <Route
                                element={<Destinations />}
                                path="destinations"
                            />
                            <Route
                                element={<EditSource />}
                                path="sources/edit/:id"
                            />
                            <Route
                                element={<EditDestination />}
                                path="destinations/edit/:id"
                            />
                            <Route
                                element={<SingleTracker />}
                                path="tracker/:id"
                            />
                            <Route
                                element={<TrackerReports />}
                                path="tracker-reports/:id"
                            />
                            <Route element={<SignalWizard />} path="wizard" />
                            <Route
                                element={<TrackerEventsList />}
                                path="tracker-events"
                            />
                            <Route
                                element={<TrackerEventMappingForm />}
                                path="tracker-events/create"
                            />
                            <Route
                                element={<TrackerEventMappingForm />}
                                path="tracker-events/edit/:id"
                            />
                            <Route
                                element={<TrackerSetting />}
                                path="tracker-setting/:id"
                            />
                            <Route
                                element={<LeadsWizardComponent />}
                                path="/leads-wizard"
                            />
                            <Route
                                element={<ConnectionDetail />}
                                path="/connection/:id"
                            />
                            <Route
                                element={<Connection />}
                                path="/lead-connections"
                            />
                            <Route
                                element={<LeadHistoryPage />}
                                path="/lead-connection-reports/:id/history"
                            />
                            <Route
                                element={<LeadConnectionReportsPage />}
                                path="/lead-connection-reports/:id"
                            />
                            <Route
                                element={<LeadGeneralReportsPage />}
                                path="/lead-general-reports"
                            />
                            <Route
                                element={<LeadDebugPage />}
                                path="/lead-debug/:id"
                            />
                            <Route
                                element={<LeadSources />}
                                path="/lead-sources"
                            />
                            <Route
                                element={<LeadDestinations />}
                                path="/lead-destinations"
                            />
                            <Route
                                element={<Services />}
                                path="/lead-services"
                            />
                            <Route
                                element={<NotificationsPage />}
                                path="/notification-view"
                            />
                            <Route
                                element={<ActivityLog />}
                                path="/activity-log"
                            />
                            <Route
                                element={<LandingPages />}
                                path="/landing-pages"
                            />
                            <Route
                                element={<LandingPageReportsPage />}
                                path="/landing-page-reports"
                            />
                            <Route
                                element={<AddEditLandingPage />}
                                path="/landing-pages/edit/:id"
                            />
                            <Route
                                element={<AddEditLandingPage />}
                                path="/landing-pages/clone"
                            />
                            <Route
                                element={<AddEditLandingPage />}
                                path="/create-landing-page"
                            />
                            <Route
                                element={<HTMLEditor />}
                                path="/custom-html-editor"
                            />

                            <Route element={<ErrorPage />} path="/error-page" />
                            <Route element={<ErrorPage />} path="*" />
                        </Routes>
                    </React.Suspense>
                    {!(
                        isFullScreenRoute ||
                        location.pathname === "/leads-wizard"
                    ) && (
                        <div className="footer-comp mt-5">
                            <Footer />
                        </div>
                    )}
                </div>
            </Container>
            {!isProduction && <SwalDevPanel />}
        </div>
    );
};

export default Main;
